import React from 'react';
import { X, Keyboard } from 'lucide-react';

/**
 * Shortcut Help Modal
 * 
 * Displays all available keyboard shortcuts grouped by context.
 * Triggered by F1 key or "?" icon in header.
 */
export const ShortcutHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcuts = {
    global: [
      { key: 'F1', description: 'Diese Hilfe öffnen' },
      { key: 'F2', description: 'Zur Kasse wechseln' },
      { key: 'F3', description: 'Zur Bestellübersicht wechseln' },
      { key: 'ESC', description: 'Modal / Dialog schließen' },
    ],
    pos: [
      { key: '/', description: 'Produktsuche fokussieren' },
      { key: '↑ / ↓', description: 'Warenkorb-Zeile fokussieren' },
      { key: 'ENTF', description: 'Fokussierten Artikel löschen' },
      { key: '+ / -', description: 'Menge des fokussierten Artikels ändern' },
      { key: 'F8', description: 'Zahlungsmodal öffnen (wenn Warenkorb nicht leer)' },
      { key: 'B', description: 'Barzahlung (nur im Zahlungsmodal)' },
      { key: 'K', description: 'Kartenzahlung (nur im Zahlungsmodal)' },
      { key: 'ENTER', description: 'Zahlung abschließen (nur im Zahlungsmodal, wenn Betrag valide)' },
    ],
    orders: [
      { key: '/', description: 'Suche fokussieren' },
      { key: '1', description: 'Status → Eingegangen' },
      { key: '2', description: 'Status → In Zubereitung' },
      { key: '3', description: 'Status → Bereit' },
      { key: '4', description: 'Status → Unterwegs' },
      { key: '5', description: 'Status → Abgeschlossen' },
      { key: 'ENTF', description: 'Fahrer-Zuordnung entfernen (Unassign)' },
      { key: 'F7', description: 'Storno-Flow öffnen' },
      { key: 'ENTER', description: 'Bestellung erweitern/schließen' },
    ],
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
      data-testid="shortcut-help-modal"
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--border-primary)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Keyboard size={24} style={{ color: 'var(--accent-primary)' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Tastatur-Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-help-modal"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all var(--transition-fast)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-6)' }}>
          {/* Global Shortcuts */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-3)' }}>
              Global (überall verfügbar)
            </h3>
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {shortcuts.global.map((shortcut, idx) => (
                <ShortcutRow key={idx} shortcut={shortcut.key} description={shortcut.description} />
              ))}
            </div>
          </div>

          {/* POS Shortcuts */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-3)' }}>
              Kasse (POS)
            </h3>
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {shortcuts.pos.map((shortcut, idx) => (
                <ShortcutRow key={idx} shortcut={shortcut.key} description={shortcut.description} />
              ))}
            </div>
          </div>

          {/* Order Overview Shortcuts */}
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-3)' }}>
              Bestellübersicht
            </h3>
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              {shortcuts.orders.map((shortcut, idx) => (
                <ShortcutRow key={idx} shortcut={shortcut.key} description={shortcut.description} />
              ))}
            </div>
          </div>

          {/* Footer Note */}
          <div
            style={{
              marginTop: 'var(--space-6)',
              padding: 'var(--space-4)',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <strong style={{ color: 'var(--text-primary)' }}>Hinweis:</strong> Shortcuts funktionieren nicht, während Sie in ein Eingabefeld tippen.
            Drücken Sie ESC, um aus einem Modal zu kommen, oder Tab, um zwischen Feldern zu wechseln.
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper component for individual shortcut rows
const ShortcutRow = ({ shortcut, description }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-3)',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <kbd
        style={{
          minWidth: '80px',
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'monospace',
          color: 'var(--text-primary)',
          textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        }}
      >
        {shortcut}
      </kbd>
      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{description}</span>
    </div>
  );
};
